import { Package } from "lucide-react";
import type { User } from "@supabase/supabase-js";
import type { Subscription } from "../types";
import { Button } from "@/components/ui/button";
import { UserAvatar } from "./UserAvatar";

interface HeaderProps {
  user?: User | null;
  onSettingsOpen?: () => void;
  subscription?: Subscription | null;
  selectionMode?: boolean;
  onToggleSelectionMode?: () => void;
  usageInfo?: {
    remaining: number;
    isUnlimited: boolean;
    total: number;
    planType: string;
  } | null;
}

export function Header({
  user,
  onSettingsOpen,
  selectionMode = false,
  onToggleSelectionMode,
  usageInfo,
}: HeaderProps) {
  return (
    <header className="px-6 py-4 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <h1 className="text-xl font-semibold text-foreground">Bardus AI</h1>

          {/* Usage Info */}
          {usageInfo && (
            <div className="hidden md:flex items-center space-x-2 text-sm text-muted-foreground">
              <span>•</span>
              <span>
                {usageInfo.isUnlimited
                  ? "Unlimited generations"
                  : `${usageInfo.remaining}/${usageInfo.total} generations remaining`}
              </span>
              <span className="px-2 py-1 bg-muted rounded-md text-xs font-medium">
                {usageInfo.planType}
              </span>
            </div>
          )}
        </div>

        {/* Right Actions */}
        <div className="flex items-center space-x-3">
          {onToggleSelectionMode && (
            <Button
              variant={selectionMode ? "default" : "outline"}
              size="sm"
              onClick={onToggleSelectionMode}
              className="flex items-center gap-2"
            >
              <Package size={16} />
              <span className="hidden sm:inline">
                {selectionMode ? "Cancel" : "Export"}
              </span>
            </Button>
          )}

          {user && onSettingsOpen && (
            <UserAvatar user={user} onOpenSettings={onSettingsOpen} />
          )}
        </div>
      </div>
    </header>
  );
}
